import React from 'react';
import { Calendar, Users, MessageSquare, TrendingDown, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { StatCard } from '../ui/StatCard';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useAppointments } from '../../hooks/useAppointments';

export const Dashboard: React.FC = () => {
  const { analytics, loading: analyticsLoading } = useAnalytics();
  const { appointments } = useAppointments();

  // Get today's appointments only
  const today = new Date().toISOString().split('T')[0];
  const todayAppointments = appointments.filter(apt => 
    apt.appointmentDate === today && apt.status === 'scheduled'
  );

  const highRiskAppointments = appointments.filter(apt => 
    apt.riskLevel === 'high' && apt.status === 'scheduled'
  );

  if (analyticsLoading || !analytics) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>
        <p className="text-gray-600 mt-2">Monitor appointment attendance and AI predictions</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Appointments"
          value={analytics.totalAppointments}
          change={8.2}
          icon={Calendar}
          color="blue"
        />
        
        <StatCard
          title="No-Show Rate"
          value={analytics.noShowRate}
          change={analytics.previousNoShowRate - analytics.noShowRate}
          icon={TrendingDown}
          color="green"
          suffix="%"
        />
        
        <StatCard
          title="Messages Sent"
          value={analytics.messagesSent}
          change={12.5}
          icon={MessageSquare}
          color="purple"
        />
        
        <StatCard
          title="Delivery Rate"
          value={analytics.messageDeliveryRate}
          change={2.3}
          icon={CheckCircle}
          color="green"
          suffix="%"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Distribution</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="font-medium text-green-800">Low Risk</span>
              </div>
              <span className="text-green-800 font-bold">{analytics.appointmentsByRisk.low}</span>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="font-medium text-yellow-800">Medium Risk</span>
              </div>
              <span className="text-yellow-800 font-bold">{analytics.appointmentsByRisk.medium}</span>
            </div>
            
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="font-medium text-red-800">High Risk</span>
              </div>
              <span className="text-red-800 font-bold">{analytics.appointmentsByRisk.high}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Today's Schedule
          </h3>
          
          {todayAppointments.length > 0 ? (
            <div className="space-y-3">
              {todayAppointments.slice(0, 5).map(apt => (
                <div key={apt.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{apt.appointmentTime}</p>
                    <p className="text-sm text-gray-600">{apt.department} - {apt.visitType}</p>
                    <p className="text-xs text-gray-500">{apt.patientId}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      apt.riskLevel === 'high' 
                        ? 'bg-red-100 text-red-800' 
                        : apt.riskLevel === 'medium' 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-green-100 text-green-800'
                    }`}>
                      {apt.riskLevel} ({apt.noShowProbability}%)
                    </div>
                  </div>
                </div>
              ))}
              {todayAppointments.length > 5 && (
                <div className="text-center pt-2">
                  <p className="text-sm text-gray-500">
                    +{todayAppointments.length - 5} more appointments today
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No appointments scheduled for today</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">High Risk Alerts</h3>
        <div className="space-y-3">
          {highRiskAppointments.slice(0, 3).map(apt => (
            <div key={apt.id} className="flex items-center space-x-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-red-900">
                  Patient {apt.patientId} - {apt.department}
                </p>
                <p className="text-sm text-red-700">
                  {apt.appointmentDate} at {apt.appointmentTime} - {apt.noShowProbability}% no-show probability
                </p>
              </div>
              <div className="flex items-center space-x-2 text-sm text-red-700">
                <Clock className="w-4 h-4" />
                <span>{apt.remindersSent} reminders sent</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};